import { fetchAuthSession } from '@aws-amplify/auth';
import type {
  ActiveBroadcast,
  Announcement,
  BoxScore,
  DefensiveAssignments,
  EventRsvp,
  FanMembership,
  FanStatus,
  Game,
  GameState,
  GameStatus,
  GameType,
  HomeOrAway,
  Lineup,
  LivePrivacy,
  LiveStatus,
  LiveToken,
  ListGamesResponse,
  Opponent,
  PitchKind,
  PlayKind,
  Player,
  Position,
  ScoreEvent,
  ScoreEventType,
  Season,
  ScorebookEventInputV2,
  ScorebookEventV2,
  HalfInning,
  ScorebookSide,
  ScorebookStateV2,
  Team,
  TeamEvent,
  TeamEventType,
  TeamUniform,
  TeamInvite,
  TeamMember,
  TeamMemberRole,
  TeamUser,
  TeamUserRole,
  Tournament,
  Notification,
  UserProfile,
  PlayerCareerStats,
  PlayerGameStats,
  PlayerPrivateProfile,
  VideoClip,
} from '../types';
import { authConfigured } from '../auth/config';
import {
  mockGameById,
  mockGameEvents,
  mockGameState,
  mockLiveStatus,
  mockListGames,
  mockPlayers,
  mockTeams,
} from './mockData';

const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');
const USE_MOCKS = BASE === '';
const homeSide: ScorebookSide = 'HOME';
const bottomHalf: HalfInning = 'BOT';

if (USE_MOCKS && import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.info(
    '[api] VITE_API_BASE is empty — serving mock data. Set it to point at the deployed HTTP API to see real data.',
  );
}

async function authHeader(): Promise<Record<string, string>> {
  if (!authConfigured) return {};
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  auth?: boolean;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.auth) Object.assign(headers, await authHeader());

  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 204) return undefined as T;
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) msg = j.error;
    } catch {
      // ignore
    }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

const delay = <T>(value: T, ms = 200): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

// ---- Public endpoints ------------------------------------------------------

export type PublicTeamSummary = Pick<
  Team,
  | 'teamId'
  | 'name'
  | 'slug'
  | 'logoUrl'
  | 'primaryColor'
  | 'secondaryColor'
  | 'season'
  | 'city'
  | 'state'
  | 'leagueName'
  | 'division'
  | 'homeFieldName'
  | 'homeFieldAddress'
  | 'contactEmail'
  | 'contactPhone'
>;

export const api = {
  listTeamsPublic: (): Promise<{ teams: PublicTeamSummary[] }> => {
    if (USE_MOCKS) return delay({ teams: mockTeams });
    return request<{ teams: PublicTeamSummary[] }>('/teams');
  },

  /** Default-team live status. Prefer the team-scoped variant when teamId is known. */
  liveStatus: (teamId?: string): Promise<LiveStatus> => {
    if (USE_MOCKS) return delay(mockLiveStatus);
    return teamId
      ? request<LiveStatus>(`/teams/${encodeURIComponent(teamId)}/live/status`)
      : request<LiveStatus>('/live/status');
  },

  /** Fetch a signed playback URL. Sends the Cognito ID token if the user is
   *  signed in (the backend ignores it for PUBLIC streams; requires it for
   *  PRIVATE). Throws if PRIVATE and unauth → caller should prompt sign-in. */
  liveToken: (teamId?: string): Promise<LiveToken> =>
    teamId
      ? request<LiveToken>(`/teams/${encodeURIComponent(teamId)}/live/token`, {
          auth: true,
        })
      : request<LiveToken>('/live/token', { auth: true }),

  listGames: (teamIdOrCursor?: string, cursor?: string): Promise<ListGamesResponse> => {
    // Back-compat: the v1 site called listGames(cursor). The team-aware site
    // calls listGames(teamId, cursor?).
    if (USE_MOCKS) return delay(mockListGames);
    if (teamIdOrCursor && cursor !== undefined) {
      return request<ListGamesResponse>(
        `/teams/${encodeURIComponent(teamIdOrCursor)}/games?cursor=${encodeURIComponent(cursor)}`,
      );
    }
    if (teamIdOrCursor && cursor === undefined) {
      // Heuristic: if it looks like a base64 cursor (== padding etc) treat as cursor;
      // otherwise treat as a teamId. Cursors from listGamesByTeam are base64 of JSON
      // and always include `=` padding or `+/`. Slugs don't.
      const looksLikeCursor = /[+/=]/.test(teamIdOrCursor);
      if (looksLikeCursor) {
        return request<ListGamesResponse>(
          `/games?cursor=${encodeURIComponent(teamIdOrCursor)}`,
        );
      }
      return request<ListGamesResponse>(
        `/teams/${encodeURIComponent(teamIdOrCursor)}/games`,
      );
    }
    return request<ListGamesResponse>('/games');
  },

  getGame: (idOrTeamId: string, gameId?: string): Promise<Game> => {
    if (USE_MOCKS) {
      const g = mockGameById(gameId ?? idOrTeamId);
      return g ? delay(g) : Promise.reject(new Error('not found'));
    }
    if (gameId) {
      return request<Game>(
        `/teams/${encodeURIComponent(idOrTeamId)}/games/${encodeURIComponent(gameId)}`,
      );
    }
    return request<Game>(`/games/${encodeURIComponent(idOrTeamId)}`);
  },

  /** Public read of the current game scoreboard state (for the live + VOD overlay). */
  gameState: (gameId: string): Promise<GameState> => {
    if (USE_MOCKS) return delay(mockGameState(gameId));
    return request<GameState>(`/games/${encodeURIComponent(gameId)}/state`);
  },

  /** Public read of the score event stream — used by the VOD overlay to
   *  project state at video.currentTime. */
  gameEvents: (gameId: string): Promise<{ events: ScoreEvent[] }> => {
    if (USE_MOCKS) return delay({ events: mockGameEvents(gameId) });
    return request<{ events: ScoreEvent[] }>(
      `/games/${encodeURIComponent(gameId)}/events`,
    );
  },

  /** Fetch a (possibly signed) VOD URL for a game. PRIVATE games require an
   *  authenticated fan with APPROVED membership; PUBLIC games return a
   *  short-lived signed URL to anyone. The `signed` boolean tells the caller
   *  whether the URL was actually signed (false during the migration window
   *  where the keypair hasn't been provisioned yet). */
  vodUrl: (
    gameIdOrTeamId: string,
    gameId?: string,
  ): Promise<{ url: string; expiresAt: string; signed: boolean }> => {
    if (USE_MOCKS) {
      const game = mockGameById(gameId ?? gameIdOrTeamId);
      return game?.vodUrl
        ? delay({
            url: game.vodUrl,
            expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
            signed: false,
          })
        : Promise.reject(new Error('recording not ready'));
    }
    const path = gameId
      ? `/teams/${encodeURIComponent(gameIdOrTeamId)}/games/${encodeURIComponent(gameId)}/vod-url`
      : `/games/${encodeURIComponent(gameIdOrTeamId)}/vod-url`;
    return request<{ url: string; expiresAt: string; signed: boolean }>(path, {
      auth: true,
    });
  },
};

export const member = {
  profile: () =>
    request<UserProfile>('/me/profile', {
      auth: true,
    }),
  updateProfile: (
    input: Partial<
      Record<
        keyof Pick<
          UserProfile,
          | 'firstName'
          | 'lastName'
          | 'displayName'
          | 'avatarKey'
          | 'phone'
          | 'city'
          | 'state'
          | 'timezone'
        >,
        string | null
      >
    >,
  ) =>
    request<UserProfile>('/me/profile', {
      method: 'PATCH',
      body: input,
      auth: true,
    }),
  createAssetUploadUrl: (input: { type: 'userAvatar'; contentType: string }) =>
    request<{ uploadUrl: string; publicUrl: string; key: string }>(
      '/me/assets/upload-url',
      { method: 'POST', body: input, auth: true },
    ),
  myTeams: () =>
    request<{ teams: { member: TeamMember; team?: Team }[] }>('/me/teams', {
      auth: true,
    }),
  notifications: () =>
    request<{ notifications: Notification[] }>('/me/notifications', { auth: true }),
  markNotificationRead: (notificationId: string) =>
    request<void>(
      `/me/notifications/${encodeURIComponent(notificationId)}/read`,
      { method: 'POST', auth: true },
    ),
  acceptInvite: (input: { teamId: string; token: string }) =>
    request<{ invite: TeamInvite; member: TeamMember }>('/me/invites/accept', {
      method: 'POST',
      body: input,
      auth: true,
    }),
  events: (teamId: string) =>
    request<{ events: TeamEvent[] }>(
      `/teams/${encodeURIComponent(teamId)}/events`,
      { auth: true },
    ),
  rsvp: (
    teamId: string,
    eventId: string,
    body: { status: EventRsvp['status']; playerId?: string; note?: string },
  ) =>
    request<EventRsvp>(
      `/teams/${encodeURIComponent(teamId)}/events/${encodeURIComponent(eventId)}/rsvp`,
      { method: 'PUT', body, auth: true },
    ),
  player: (teamId: string, playerId: string) =>
    request<Player>(
      `/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}`,
      { auth: true },
    ),
  playerClips: (teamId: string, playerId: string) =>
    request<{ clips: VideoClip[] }>(
      `/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}/clips`,
      { auth: true },
    ),
  playerBirthCertificateUrl: (teamId: string, playerId: string) =>
    request<{ url: string }>(
      `/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}/birth-certificate/url`,
      { auth: true },
    ),
};

// ---- Admin endpoints (require Authorization: Bearer <jwt>) -----------------

export interface CreateTeamInput {
  name: string;
  slug?: string;
  logoKey?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  season?: string;
  city?: string;
  state?: string;
  leagueName?: string;
  division?: string;
  homeFieldName?: string;
  homeFieldAddress?: string;
  contactEmail?: string;
  contactPhone?: string;
  adminNotes?: string;
}

export interface UpdateTeamInput {
  name?: string;
  logoKey?: string | null;
  logoUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  season?: string | null;
  city?: string | null;
  state?: string | null;
  leagueName?: string | null;
  division?: string | null;
  homeFieldName?: string | null;
  homeFieldAddress?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  adminNotes?: string | null;
  active?: boolean;
  channelArn?: string | null;
  playbackUrl?: string | null;
}

export interface CreatePlayerInput {
  firstName: string;
  lastName: string;
  jerseyNumber: string;
  primaryPositions?: Position[];
  photoUrl?: string;
  preferredName?: string;
  bats?: Player['bats'];
  throws?: Player['throws'];
  heightInches?: number;
  weightLbs?: number;
  birthdate?: string;
  graduationYear?: number;
  profileVisibility?: Player['profileVisibility'];
  bio?: string;
  guardianMemberIds?: string[];
  privateProfile?: PlayerPrivateProfile;
}

export interface UpdatePlayerInput {
  firstName?: string;
  lastName?: string;
  jerseyNumber?: string;
  primaryPositions?: Position[];
  photoUrl?: string | null;
  preferredName?: string | null;
  bats?: Player['bats'] | null;
  throws?: Player['throws'] | null;
  heightInches?: number | null;
  weightLbs?: number | null;
  birthdate?: string | null;
  graduationYear?: number | null;
  profileVisibility?: Player['profileVisibility'] | null;
  bio?: string | null;
  guardianMemberIds?: string[];
  privateProfile?: PlayerPrivateProfile | null;
  active?: boolean;
}

export interface CreateOpponentInput {
  name: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  notes?: string;
}

export interface UpdateOpponentInput {
  name?: string;
  logoUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  notes?: string | null;
}

export interface ScheduleGameInput {
  scheduledAt: string;
  opponent: string;
  opponentId?: string;
  venue?: string;
  warmupAt?: string;
  warmupLocationName?: string;
  warmupAddress?: string;
  warmupLatitude?: number;
  warmupLongitude?: number;
  homeOrAway?: HomeOrAway;
  gameType?: GameType;
  seasonId?: string;
  tournamentId?: string;
  uniformId?: string;
  livePrivacy?: LivePrivacy;
  aiAssisted?: boolean;
  notes?: string;
}

export interface UpdateGameInput {
  scheduledAt?: string;
  opponent?: string;
  opponentId?: string | null;
  venue?: string | null;
  warmupAt?: string | null;
  warmupLocationName?: string | null;
  warmupAddress?: string | null;
  warmupLatitude?: number | null;
  warmupLongitude?: number | null;
  homeOrAway?: HomeOrAway | null;
  gameType?: GameType;
  seasonId?: string | null;
  tournamentId?: string | null;
  uniformId?: string | null;
  livePrivacy?: LivePrivacy;
  aiAssisted?: boolean;
  notes?: string | null;
  status?: GameStatus;
}

export interface SeasonInput {
  name: string;
  startDate: string;
  endDate: string;
  notes?: string;
}

export interface UpdateSeasonInput {
  name?: string;
  startDate?: string;
  endDate?: string;
  active?: boolean;
  notes?: string | null;
}

export interface TournamentInput {
  name: string;
  startDate: string;
  endDate: string;
  venue?: string;
  seasonId?: string;
  notes?: string;
}

export interface UpdateTournamentInput {
  name?: string;
  startDate?: string;
  endDate?: string;
  venue?: string | null;
  seasonId?: string | null;
  notes?: string | null;
}

export interface MemberInput {
  memberId?: string;
  userId?: string;
  email?: string;
  displayName?: string;
  roles: TeamMemberRole[];
  status?: TeamMember['status'];
  linkedPlayerIds?: string[];
  relationship?: string;
}

export interface TeamUniformInput {
  name: string;
  jerseyColor?: string;
  pantsColor?: string;
  hatColor?: string;
  socksColor?: string;
  beltColor?: string;
  notes?: string;
  active?: boolean;
}

export interface UpdateTeamUniformInput {
  name?: string;
  jerseyColor?: string | null;
  pantsColor?: string | null;
  hatColor?: string | null;
  socksColor?: string | null;
  beltColor?: string | null;
  notes?: string | null;
  active?: boolean;
}

export interface TeamEventInput {
  type: TeamEventType;
  title: string;
  startAt: string;
  endAt?: string | null;
  arrivalAt?: string | null;
  warmupAt?: string | null;
  warmupLocationName?: string | null;
  warmupAddress?: string | null;
  warmupLatitude?: number | null;
  warmupLongitude?: number | null;
  locationName?: string | null;
  address?: string | null;
  opponent?: string | null;
  opponentId?: string | null;
  homeOrAway?: HomeOrAway;
  gameId?: string;
  seasonId?: string | null;
  tournamentId?: string | null;
  uniformId?: string | null;
  rsvpEnabled?: boolean;
  status?: TeamEvent['status'];
  notes?: string | null;
  notifyTeam?: boolean;
  recurrence?: TeamEvent['recurrence'];
}

export const admin = {
  whoami: () => request<{ userId: string; email?: string; groups: string[] }>(
    '/admin/whoami',
    { auth: true },
  ),

  // Teams
  listTeams: () => {
    if (USE_MOCKS) return delay({ teams: mockTeams });
    return request<{ teams: Team[] }>('/admin/teams', { auth: true });
  },
  createTeam: (input: CreateTeamInput) =>
    request<Team>('/admin/teams', { method: 'POST', body: input, auth: true }),
  getTeam: (teamId: string) =>
    USE_MOCKS
      ? delay(mockTeams.find((team) => team.teamId === teamId) ?? mockTeams[0])
      : request<Team>(`/admin/teams/${encodeURIComponent(teamId)}`, { auth: true }),
  updateTeam: (teamId: string, input: UpdateTeamInput) =>
    request<Team>(`/admin/teams/${encodeURIComponent(teamId)}`, {
      method: 'PATCH',
      body: input,
      auth: true,
    }),
  createAssetUploadUrl: (
    teamId: string,
    input: {
      type: 'teamLogo' | 'playerAvatar' | 'playerBirthCertificate';
      contentType: string;
      playerId?: string;
    },
  ) =>
    request<{ uploadUrl: string; publicUrl?: string; key: string }>(
      `/admin/teams/${encodeURIComponent(teamId)}/assets/upload-url`,
      { method: 'POST', body: input, auth: true },
    ),
  deleteTeam: (teamId: string) =>
    request<void>(`/admin/teams/${encodeURIComponent(teamId)}`, {
      method: 'DELETE',
      auth: true,
    }),
  /** Rotate the team's IVS stream key. Returns the new value (one-shot;
   *  there is no way to retrieve it again afterwards without rotating). */
  rotateStreamKey: (teamId: string) =>
    request<{ streamKey: string }>(
      `/admin/teams/${encodeURIComponent(teamId)}/rotate-stream-key`,
      { method: 'POST', auth: true },
    ),

  // Players
  listPlayers: (teamId: string) =>
    USE_MOCKS
      ? delay({ players: mockPlayers.filter((player) => player.teamId === teamId) })
      : request<{ players: Player[] }>(
          `/admin/teams/${encodeURIComponent(teamId)}/players`,
          { auth: true },
        ),
  getPlayer: (teamId: string, playerId: string) =>
    request<Player>(
      `/admin/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}`,
      { auth: true },
    ),
  createPlayer: (teamId: string, input: CreatePlayerInput) =>
    request<Player>(`/admin/teams/${encodeURIComponent(teamId)}/players`, {
      method: 'POST',
      body: input,
      auth: true,
    }),
  updatePlayer: (teamId: string, playerId: string, input: UpdatePlayerInput) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}`,
      { method: 'PATCH', body: input, auth: true },
    ),
  deletePlayer: (teamId: string, playerId: string) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}`,
      { method: 'DELETE', auth: true },
    ),
  getPlayerBirthCertificateUrl: (teamId: string, playerId: string) =>
    request<{ url: string }>(
      `/admin/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}/birth-certificate/url`,
      { auth: true },
    ),
  getPlayerCareerStats: (teamId: string, playerId: string) =>
    request<PlayerCareerStats>(
      `/admin/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}/stats`,
      { auth: true },
    ),

  // Opponents
  listOpponents: (teamId: string) =>
    request<{ opponents: Opponent[] }>(
      `/admin/teams/${encodeURIComponent(teamId)}/opponents`,
      { auth: true },
    ),
  createOpponent: (teamId: string, input: CreateOpponentInput) =>
    request<Opponent>(`/admin/teams/${encodeURIComponent(teamId)}/opponents`, {
      method: 'POST',
      body: input,
      auth: true,
    }),
  updateOpponent: (teamId: string, opponentId: string, input: UpdateOpponentInput) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/opponents/${encodeURIComponent(opponentId)}`,
      { method: 'PATCH', body: input, auth: true },
    ),
  deleteOpponent: (teamId: string, opponentId: string) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/opponents/${encodeURIComponent(opponentId)}`,
      { method: 'DELETE', auth: true },
    ),

  // Team users
  listTeamUsers: (teamId: string) =>
    request<{ users: TeamUser[] }>(
      `/admin/teams/${encodeURIComponent(teamId)}/users`,
      { auth: true },
    ),
  assignTeamUser: (teamId: string, userId: string, role: TeamUserRole) =>
    request<TeamUser>(`/admin/teams/${encodeURIComponent(teamId)}/users`, {
      method: 'POST',
      body: { userId, role },
      auth: true,
    }),
  removeTeamUser: (teamId: string, userId: string, role: TeamUserRole) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/users/${encodeURIComponent(userId)}/${role}`,
      { method: 'DELETE', auth: true },
    ),

  // vNext members / invites
  listMembers: (teamId: string) =>
    request<{ members: TeamMember[] }>(
      `/admin/teams/${encodeURIComponent(teamId)}/members`,
      { auth: true },
    ),
  upsertMember: (teamId: string, input: MemberInput) =>
    request<TeamMember>(`/admin/teams/${encodeURIComponent(teamId)}/members`, {
      method: 'POST',
      body: input,
      auth: true,
    }),
  setMemberStatus: (teamId: string, memberId: string, status: TeamMember['status']) =>
    request<TeamMember>(
      `/admin/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(memberId)}`,
      { method: 'PATCH', body: { status }, auth: true },
    ),
  listInvites: (teamId: string) =>
    request<{ invites: TeamInvite[] }>(
      `/admin/teams/${encodeURIComponent(teamId)}/invites`,
      { auth: true },
    ),
  createInvite: (
    teamId: string,
    input: { email: string; roles: TeamMemberRole[]; linkedPlayerIds?: string[]; expiresAt?: string },
  ) =>
    request<{ invite: TeamInvite; token: string }>(
      `/admin/teams/${encodeURIComponent(teamId)}/invites`,
      { method: 'POST', body: input, auth: true },
    ),

  listUniforms: (teamId: string) =>
    request<{ uniforms: TeamUniform[] }>(
      `/admin/teams/${encodeURIComponent(teamId)}/uniforms`,
      { auth: true },
    ),
  createUniform: (teamId: string, input: TeamUniformInput) =>
    request<TeamUniform>(`/admin/teams/${encodeURIComponent(teamId)}/uniforms`, {
      method: 'POST',
      body: input,
      auth: true,
    }),
  updateUniform: (teamId: string, uniformId: string, input: UpdateTeamUniformInput) =>
    request<TeamUniform>(
      `/admin/teams/${encodeURIComponent(teamId)}/uniforms/${encodeURIComponent(uniformId)}`,
      { method: 'PATCH', body: input, auth: true },
    ),

  // vNext calendar / RSVP / announcements
  listEvents: (teamId: string) =>
    request<{ events: TeamEvent[] }>(
      `/admin/teams/${encodeURIComponent(teamId)}/events`,
      { auth: true },
    ),
  getEvent: (teamId: string, eventId: string) =>
    request<TeamEvent>(
      `/admin/teams/${encodeURIComponent(teamId)}/events/${encodeURIComponent(eventId)}`,
      { auth: true },
    ),
  createEvent: (teamId: string, input: TeamEventInput) =>
    request<TeamEvent>(`/admin/teams/${encodeURIComponent(teamId)}/events`, {
      method: 'POST',
      body: input,
      auth: true,
    }),
  updateEvent: (teamId: string, eventId: string, input: Partial<TeamEventInput>) =>
    request<TeamEvent>(
      `/admin/teams/${encodeURIComponent(teamId)}/events/${encodeURIComponent(eventId)}`,
      { method: 'PATCH', body: input, auth: true },
    ),
  listEventRsvps: (teamId: string, eventId: string) =>
    request<{ rsvps: EventRsvp[]; matrix: Array<{ player: Player; status: EventRsvp['status']; rsvps: EventRsvp[] }> }>(
      `/admin/teams/${encodeURIComponent(teamId)}/events/${encodeURIComponent(eventId)}/rsvps`,
      { auth: true },
    ),
  putEventRsvp: (
    teamId: string,
    eventId: string,
    input: { memberId: string; userId?: string; playerId?: string; status: EventRsvp['status']; note?: string },
  ) =>
    request<EventRsvp>(
      `/admin/teams/${encodeURIComponent(teamId)}/events/${encodeURIComponent(eventId)}/rsvps`,
      { method: 'PUT', body: input, auth: true },
    ),
  getLineupSources: (teamId: string, gameId: string) =>
    request<{
      activePlayers: Player[];
      previousGames: Array<{ game: Game; lineup: Lineup | null }>;
    }>(
      `/admin/teams/${encodeURIComponent(teamId)}/games/${encodeURIComponent(gameId)}/lineup-sources`,
      { auth: true },
    ),
  importLineup: (
    teamId: string,
    gameId: string,
    input: { source: 'ACTIVE_ROSTER' | 'PREVIOUS_GAME' | 'BLANK'; previousGameId?: string },
  ) =>
    request<{ lineup: Lineup; warnings: string[] }>(
      `/admin/teams/${encodeURIComponent(teamId)}/games/${encodeURIComponent(gameId)}/lineup/import`,
      { method: 'POST', body: input, auth: true },
    ),
  listAnnouncements: (teamId: string) =>
    request<{ announcements: Announcement[] }>(
      `/admin/teams/${encodeURIComponent(teamId)}/announcements`,
      { auth: true },
    ),
  createAnnouncement: (
    teamId: string,
    input: { title: string; body: string; audience?: Announcement['audience']; playerIds?: string[]; email?: boolean },
  ) =>
    request<Announcement>(
      `/admin/teams/${encodeURIComponent(teamId)}/announcements`,
      { method: 'POST', body: input, auth: true },
    ),
  listTeamStats: (teamId: string) =>
    request<{ stats: PlayerGameStats[] }>(
      `/admin/teams/${encodeURIComponent(teamId)}/stats`,
      { auth: true },
    ),

  // Games (admin)
  listGames: (teamId: string, statuses?: GameStatus[]) => {
    if (USE_MOCKS) {
      const games = mockListGames.games.filter((game) => {
        if (game.teamId !== teamId) return false;
        return !statuses?.length || statuses.includes(game.status);
      });
      return delay({ games });
    }
    const qs = statuses && statuses.length > 0 ? `?status=${statuses.join(',')}` : '';
    return request<{ games: Game[]; cursor?: string }>(
      `/admin/teams/${encodeURIComponent(teamId)}/games${qs}`,
      { auth: true },
    );
  },
  scheduleGame: (teamId: string, input: ScheduleGameInput) =>
    request<Game>(`/admin/teams/${encodeURIComponent(teamId)}/games`, {
      method: 'POST',
      body: input,
      auth: true,
    }),
  getGame: (teamId: string, gameId: string) =>
    USE_MOCKS
      ? delay(mockGameById(gameId) ?? mockListGames.games[0])
      : request<Game>(
          `/admin/teams/${encodeURIComponent(teamId)}/games/${encodeURIComponent(gameId)}`,
          { auth: true },
        ),
  updateGame: (teamId: string, gameId: string, input: UpdateGameInput) =>
    request<Game>(
      `/admin/teams/${encodeURIComponent(teamId)}/games/${encodeURIComponent(gameId)}`,
      { method: 'PATCH', body: input, auth: true },
    ),
  deleteGame: (teamId: string, gameId: string) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/games/${encodeURIComponent(gameId)}`,
      { method: 'DELETE', auth: true },
    ),

  // Broadcast control
  getActiveBroadcast: (teamId: string) =>
    request<{ activeBroadcast: ActiveBroadcast | null }>(
      `/admin/teams/${encodeURIComponent(teamId)}/broadcast`,
      { auth: true },
    ),
  startBroadcast: (teamId: string, gameId: string) =>
    request<{ activeBroadcast: ActiveBroadcast }>(
      `/admin/teams/${encodeURIComponent(teamId)}/broadcast/start`,
      { method: 'POST', body: { gameId }, auth: true },
    ),
  stopBroadcast: (teamId: string) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/broadcast/stop`,
      { method: 'POST', auth: true },
    ),
  getIngestInfo: (teamId: string) =>
    request<{ ingestEndpoint: string; streamKey: string }>(
      `/admin/teams/${encodeURIComponent(teamId)}/ingest`,
      { auth: true },
    ),

  // Seasons
  listSeasons: (teamId: string) =>
    request<{ seasons: Season[] }>(
      `/admin/teams/${encodeURIComponent(teamId)}/seasons`,
      { auth: true },
    ),
  createSeason: (teamId: string, input: SeasonInput) =>
    request<Season>(`/admin/teams/${encodeURIComponent(teamId)}/seasons`, {
      method: 'POST',
      body: input,
      auth: true,
    }),
  updateSeason: (teamId: string, seasonId: string, input: UpdateSeasonInput) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/seasons/${encodeURIComponent(seasonId)}`,
      { method: 'PATCH', body: input, auth: true },
    ),
  deleteSeason: (teamId: string, seasonId: string) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/seasons/${encodeURIComponent(seasonId)}`,
      { method: 'DELETE', auth: true },
    ),

  // Tournaments
  listTournaments: (teamId: string) =>
    request<{ tournaments: Tournament[] }>(
      `/admin/teams/${encodeURIComponent(teamId)}/tournaments`,
      { auth: true },
    ),
  createTournament: (teamId: string, input: TournamentInput) =>
    request<Tournament>(`/admin/teams/${encodeURIComponent(teamId)}/tournaments`, {
      method: 'POST',
      body: input,
      auth: true,
    }),
  updateTournament: (teamId: string, tournamentId: string, input: UpdateTournamentInput) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/tournaments/${encodeURIComponent(tournamentId)}`,
      { method: 'PATCH', body: input, auth: true },
    ),
  deleteTournament: (teamId: string, tournamentId: string) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/tournaments/${encodeURIComponent(tournamentId)}`,
      { method: 'DELETE', auth: true },
    ),

  // Score events (6E)
  postScoreEvent: (
    teamId: string,
    gameId: string,
    body: {
      type: ScoreEventType;
      pitch?: PitchKind;
      play?: PlayKind;
      runsScored?: number;
      note?: string;
      videoTimestampSec?: number;
      clientTs?: string;
    },
  ) =>
    request<ScoreEvent>(
      `/admin/teams/${encodeURIComponent(teamId)}/games/${encodeURIComponent(gameId)}/events`,
      { method: 'POST', body, auth: true },
    ),
  postScorebookEvent: (teamId: string, gameId: string, body: ScorebookEventInputV2) =>
    request<ScorebookEventV2>(
      `/admin/teams/${encodeURIComponent(teamId)}/games/${encodeURIComponent(gameId)}/scorebook`,
      { method: 'POST', body, auth: true },
    ),
  undoScorebookEvent: (teamId: string, gameId: string) =>
    request<{ state: ScorebookStateV2; removed?: ScorebookEventV2 }>(
      `/admin/teams/${encodeURIComponent(teamId)}/games/${encodeURIComponent(gameId)}/scorebook/undo`,
      { method: 'POST', auth: true },
    ),
  getScorebook: (gameId: string) =>
    USE_MOCKS
      ? delay({
          state: {
            gameId,
            inning: 4,
            halfInning: bottomHalf,
            outs: 1,
            balls: 2,
            strikes: 1,
            runsHome: 4,
            runsAway: 3,
            bases: {
              first: { playerId: 'p-7', side: homeSide },
              third: { playerId: 'p-12', side: homeSide },
            },
            currentBatterIds: { HOME: 'p-22' },
            currentPitcherIds: { AWAY: 'p-00' },
            eventCount: 4,
            ended: false,
            updatedAt: new Date().toISOString(),
          },
          events: [],
        })
      : request<{ state: ScorebookStateV2; events: ScorebookEventV2[] }>(
          `/admin/games/${encodeURIComponent(gameId)}/scorebook`,
          { auth: true },
        ),
  getBoxScore: (gameId: string) =>
    request<BoxScore | null>(`/admin/games/${encodeURIComponent(gameId)}/box-score`, {
      auth: true,
    }),
  listGameStats: (gameId: string) =>
    request<{ stats: PlayerGameStats[] }>(
      `/admin/games/${encodeURIComponent(gameId)}/stats`,
      { auth: true },
    ),
  rebuildStats: (teamId: string, gameId: string) =>
    request<{ stats: PlayerGameStats[]; boxScore: BoxScore }>(
      `/admin/teams/${encodeURIComponent(teamId)}/games/${encodeURIComponent(gameId)}/rebuild-stats`,
      { method: 'POST', auth: true },
    ),
  listClips: (gameId: string) =>
    request<{ clips: VideoClip[] }>(`/admin/games/${encodeURIComponent(gameId)}/clips`, {
      auth: true,
    }),
  generateClips: (teamId: string, gameId: string) =>
    request<{ clips: VideoClip[] }>(
      `/admin/teams/${encodeURIComponent(teamId)}/games/${encodeURIComponent(gameId)}/clips`,
      { method: 'POST', auth: true },
    ),
  getScoreState: (gameId: string) =>
    request<GameState>(`/admin/games/${encodeURIComponent(gameId)}/state`, {
      auth: true,
    }),
  listScoreEvents: (gameId: string, since?: number) => {
    const qs = since !== undefined ? `?since=${since}` : '';
    return request<{ events: ScoreEvent[] }>(
      `/admin/games/${encodeURIComponent(gameId)}/events${qs}`,
      { auth: true },
    );
  },
  undoScoreEvent: (gameId: string) =>
    request<GameState>(`/admin/games/${encodeURIComponent(gameId)}/undo`, {
      method: 'POST',
      auth: true,
    }),

  // Fan flow (6F)
  followTeam: (teamId: string) =>
    request<FanMembership>(
      `/admin/teams/${encodeURIComponent(teamId)}/follow`,
      { method: 'POST', auth: true },
    ),
  getMyFollow: (teamId: string) =>
    request<FanMembership | null>(
      `/admin/teams/${encodeURIComponent(teamId)}/follow`,
      { auth: true },
    ),
  listFans: (teamId: string, status?: FanStatus) => {
    const qs = status ? `?status=${status}` : '';
    return request<{ fans: FanMembership[] }>(
      `/admin/teams/${encodeURIComponent(teamId)}/fans${qs}`,
      { auth: true },
    );
  },
  approveFan: (teamId: string, userId: string) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/fans/${encodeURIComponent(userId)}/approve`,
      { method: 'POST', auth: true },
    ),
  denyFan: (teamId: string, userId: string) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/fans/${encodeURIComponent(userId)}/deny`,
      { method: 'POST', auth: true },
    ),
  removeFan: (teamId: string, userId: string) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/fans/${encodeURIComponent(userId)}`,
      { method: 'DELETE', auth: true },
    ),

  // Lineup
  getLineup: (gameId: string) =>
    request<Lineup | null>(`/admin/games/${encodeURIComponent(gameId)}/lineup`, {
      auth: true,
    }),
  putLineup: (
    gameId: string,
    body: { battingOrder: string[]; bench: string[]; defense: DefensiveAssignments },
  ) =>
    request<Lineup>(`/admin/games/${encodeURIComponent(gameId)}/lineup`, {
      method: 'PUT',
      body,
      auth: true,
    }),

  // Post-game AI review (6E-AI-PostGame)
  acceptScoreEvent: (gameId: string, seq: number) =>
    request<ScoreEvent>(
      `/admin/games/${encodeURIComponent(gameId)}/events/${seq}/accept`,
      { method: 'POST', auth: true },
    ),
  editScoreEvent: (
    gameId: string,
    seq: number,
    patch: {
      type?: ScoreEventType;
      pitch?: PitchKind;
      play?: PlayKind;
      runsScored?: number;
      note?: string;
      videoTimestampSec?: number;
    },
  ) =>
    request<ScoreEvent>(`/admin/games/${encodeURIComponent(gameId)}/events/${seq}`, {
      method: 'PATCH',
      body: patch,
      auth: true,
    }),
  deleteScoreEvent: (gameId: string, seq: number) =>
    request<void>(`/admin/games/${encodeURIComponent(gameId)}/events/${seq}`, {
      method: 'DELETE',
      auth: true,
    }),
  finalizeGame: (teamId: string, gameId: string) =>
    request<void>(
      `/admin/teams/${encodeURIComponent(teamId)}/games/${encodeURIComponent(gameId)}/finalize`,
      { method: 'POST', auth: true },
    ),
};
